export interface Translations {
  // Navigation
  nav: {
    home: string;
    features: string;
    pricing: string;
    docs: string;
    blog: string;
    login: string;
    register: string;
    dashboard: string;
    profile: string;
    logout: string;
  };

  // User Menu
  userMenu: {
    dashboard: string;
    imageGen: string;
    audioGen: string;
    history: string;
    settings: string;
    quota: string;
    loading: string;
  };

  // Hero Section
  hero: {
    title: string;
    subtitle: string;
    cta: {
      imageGen: string;
      audioGen: string;
      history: string;
    };
  };

  // Features
  features: {
    title: string;
    imageGen: {
      title: string;
      description: string;
    };
    audioGen: {
      title: string;
      description: string;
    };
    creativeBoost: {
      title: string;
      description: string;
    };
  };

  // Stats
  stats: {
    ai: string;
    unlimited: string;
    support: string;
    tools: string;
  };

  // Why Choose Us
  whyChooseUs: {
    title: string;
    fast: {
      title: string;
      description: string;
    };
    quality: {
      title: string;
      description: string;
    };
    multilingual: {
      title: string;
      description: string;
    };
  };

  // Footer
  footer: {
    copyright: string;
    links: {
      privacy: string;
      terms: string;
      contact: string;
      about: string;
    };
  };

  // Additional Links
  links: {
    learnMore: string;
    tutorials: string;
    apiDocs: string;
    pricing: string;
  };

  // Accessibility
  a11y: {
    skipToContent: string;
    mainNav: string;
    mainFeatures: string;
    statsSection: string;
    footerNav: string;
    pageLoaded: string;
  };
}

export const translations: Record<string, Translations> = {
  en: {
    nav: {
      home: 'Home',
      features: 'Features',
      pricing: 'Pricing',
      docs: 'Docs',
      blog: 'Blog',
      login: 'Login',
      register: 'Register',
      dashboard: 'Dashboard',
      profile: 'Profile',
      logout: 'Logout',
    },
    userMenu: {
      dashboard: 'Dashboard',
      imageGen: 'Image Generation',
      audioGen: 'Voice Synthesis',
      history: 'Creation History',
      settings: 'Account Settings',
      quota: 'Quota',
      loading: 'Loading...',
    },
    hero: {
      title: 'Keycore AI Studio - Professional AI Image Generation & Voice Synthesis Platform',
      subtitle: 'Free AI image generator powered by the latest Flux model. Intelligent voice synthesis supporting multiple languages. Unlimited creative platform, zero-threshold AI art generator.',
      cta: {
        imageGen: 'Start Creating Images',
        audioGen: 'Create Voice Content',
        history: 'View Creation History',
      },
    },
    features: {
      title: 'Core Features',
      imageGen: {
        title: 'AI Image Generator',
        description: 'High-quality AI painting tool based on Flux Schnell model, supporting text-to-image conversion, rapid generation of artistic works. AI art creation, zero-threshold creative platform.',
      },
      audioGen: {
        title: 'Intelligent Voice Synthesis',
        description: 'Advanced AI voice generation technology, supporting Chinese and English voice synthesis, natural and fluent text-to-speech service. Multi-language AI voice tool.',
      },
      creativeBoost: {
        title: 'AI Creative Enhancement',
        description: 'Intelligent prompt optimization tool to improve AI creation quality, professional art generation auxiliary platform. AI creation efficiency tool.',
      },
    },
    stats: {
      ai: 'Advanced AI Technology',
      unlimited: 'Unlimited Creative Possibilities',
      support: '24/7 Online Service',
      tools: 'Professional Creation Tools',
    },
    whyChooseUs: {
      title: 'Why Choose Keycore AI Studio?',
      fast: {
        title: '🚀 Fast Generation',
        description: 'Based on Cloudflare global network, AI image generation is extremely fast, usually completed in 10-30 seconds with high-quality creation.',
      },
      quality: {
        title: '🎨 High Quality',
        description: 'Using the latest Flux model, the generated high-quality images support 1024x1024 resolution, meeting professional creation needs.',
      },
      multilingual: {
        title: '🌐 Multilingual Support',
        description: 'Voice synthesis supports Chinese, English and other languages, natural and fluent pronunciation effects, professional audio experience.',
      },
    },
    footer: {
      copyright: '© 2025 Keycore AI Studio. Professional AI image generation and voice synthesis platform.',
      links: {
        privacy: 'Privacy Policy',
        terms: 'Terms of Service',
        contact: 'Contact Us',
        about: 'About Us',
      },
    },
    links: {
      learnMore: 'Want to learn more about AI creation techniques and best practices?',
      tutorials: 'View AI Creation Tutorials',
      apiDocs: 'API Documentation',
      pricing: 'Pricing Plans',
    },
    a11y: {
      skipToContent: 'Skip to main content',
      mainNav: 'Main navigation',
      mainFeatures: 'Main features',
      statsSection: 'Platform statistics',
      footerNav: 'Footer navigation',
      pageLoaded: 'Page loaded, welcome to use Keycore AI Studio',
    },
  },
  zh: {
    nav: {
      home: '首页',
      features: '功能',
      pricing: '定价',
      docs: '文档',
      blog: '博客',
      login: '登录',
      register: '注册',
      dashboard: '仪表板',
      profile: '个人资料',
      logout: '退出登录',
    },
    userMenu: {
      dashboard: '用户仪表板',
      imageGen: '图像生成',
      audioGen: '语音合成',
      history: '创作历史',
      settings: '账户设置',
      quota: '配额',
      loading: '加载中...',
    },
    hero: {
      title: 'Keycore AI Studio - AI图像生成 | 语音合成 | 内容创作平台',
      subtitle: '免费AI图像生成器，基于最新Flux模型。智能语音合成，支持中文英文等多种语言。AI艺术创作工具，零门槛创意平台，让每个人都能成为AI艺术家。',
      cta: {
        imageGen: '开始创作图像',
        audioGen: '创建语音内容',
        history: '查看创作历史',
      },
    },
    features: {
      title: '核心功能特性',
      imageGen: {
        title: 'AI图像生成器',
        description: '基于Flux Schnell模型的高质量AI绘画工具，支持文本转图像，快速生成艺术作品。AI艺术创作，零门槛创意平台。',
      },
      audioGen: {
        title: '智能语音合成',
        description: '先进的AI语音生成技术，支持中英文语音合成，自然流畅的文本转语音服务。多语言AI语音工具。',
      },
      creativeBoost: {
        title: 'AI创意增强',
        description: '智能提示词优化工具，提升AI创作质量，专业的艺术生成辅助平台。AI创作效率工具。',
      },
    },
    stats: {
      ai: '先进AI技术驱动',
      unlimited: '无限创意可能性',
      support: '全天候在线服务',
      tools: '专业级创作工具',
    },
    whyChooseUs: {
      title: '为什么选择 Keycore AI Studio？',
      fast: {
        title: '🚀 快速生成',
        description: '基于Cloudflare全球网络，AI图像生成速度极快，通常在10-30秒内完成高质量创作',
      },
      quality: {
        title: '🎨 高质量',
        description: '采用最新Flux模型，生成的高质量图像，支持1024x1024分辨率，满足专业创作需求',
      },
      multilingual: {
        title: '🌐 多语言支持',
        description: '语音合成支持中文、英文等多种语言，自然流畅的发音效果，专业级音频体验',
      },
    },
    footer: {
      copyright: '© 2025 Keycore AI Studio. 专业的AI图像生成和语音合成平台.',
      links: {
        privacy: '隐私政策',
        terms: '使用条款',
        contact: '联系我们',
        about: '关于我们',
      },
    },
    links: {
      learnMore: '想要了解更多AI创作技巧和最佳实践？',
      tutorials: '查看AI创作教程',
      apiDocs: 'API文档',
      pricing: '定价方案',
    },
    a11y: {
      skipToContent: '跳至主要内容',
      mainNav: '主导航',
      mainFeatures: '主要功能',
      statsSection: '平台数据统计',
      footerNav: '页脚导航',
      pageLoaded: '页面加载完成，欢迎使用 Keycore AI Studio',
    },
  },
};

export type Language = keyof typeof translations;
